// Shared UI helpers for ClientDesk
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { MoreVertical } from "lucide-react";

// ---- Centralised status system ----
// Every status shown anywhere in the app renders through this table so labels
// and colours stay identical across modules. Colour is a supplement, never
// the only signal — the readable text always renders alongside.
const NEUTRAL = "bg-[#F5F5F4] text-[#57534E] border-[#E7E5E4]";
const GREEN   = "bg-[#E8F0E4] text-[#2C3A29] border-[#B8CDB0]";
const AMBER   = "bg-[#FDE9D8] text-[#B85C1F] border-[#F5C9A6]";
const BLUE    = "bg-[#E8EFF5] text-[#1E4B7A] border-[#B9CFE1]";
const RED     = "bg-[#FDDCDC] text-[#B02222] border-[#F5B0B0]";

// value → { label, color }
export const STATUS_MAP = {
  // clients / generic
  active:                  { label: "Active",           color: GREEN },
  pending:                 { label: "Pending",          color: AMBER },
  inactive:                { label: "Inactive",         color: NEUTRAL },
  archived:                { label: "Archived",         color: NEUTRAL },
  // tasks
  todo:                    { label: "To Do",            color: NEUTRAL },
  in_progress:             { label: "In Progress",      color: AMBER },
  completed:               { label: "Completed",        color: GREEN },
  // priority
  low:                     { label: "Low",              color: NEUTRAL },
  medium:                  { label: "Medium",           color: BLUE },
  normal:                  { label: "Normal",           color: BLUE },
  high:                    { label: "High",             color: AMBER },
  urgent:                  { label: "Urgent",           color: RED },
  // appointments & bookings
  scheduled:               { label: "Scheduled",        color: NEUTRAL },
  confirmed:               { label: "Confirmed",        color: GREEN },
  cancelled:               { label: "Cancelled",        color: RED },
  no_show:                 { label: "No Show",          color: RED },
  // intake
  not_started:             { label: "Not Started",      color: NEUTRAL },
  waiting_for_information: { label: "Waiting on Info",  color: AMBER },
  complete:                { label: "Complete",         color: GREEN },
  // invoices
  draft:                   { label: "Draft",            color: NEUTRAL },
  sent:                    { label: "Sent",             color: AMBER },
  viewed:                  { label: "Viewed",           color: BLUE },
  paid:                    { label: "Paid",             color: GREEN },
  overdue:                 { label: "Overdue",          color: RED },
  void:                    { label: "Void",             color: RED },
  // contracts
  signed:                  { label: "Signed",           color: GREEN },
  expired:                 { label: "Expired",          color: RED },
  terminated:              { label: "Terminated",       color: RED },
  // service requests
  submitted:               { label: "Submitted",        color: BLUE },
  in_review:               { label: "In Review",        color: AMBER },
  assigned:                { label: "Assigned",         color: BLUE },
  // forms / onboarding
  changes_requested:       { label: "Changes Requested",color: AMBER },
  resubmitted:             { label: "Resubmitted",      color: AMBER },
  approved:                { label: "Approved",         color: GREEN },
  under_review:            { label: "Under Review",     color: AMBER },
};

export const STATUS_COLORS = Object.fromEntries(
  Object.entries(STATUS_MAP).map(([k, v]) => [k, v.color])
);

export function StatusBadge({ value, testid }) {
  if (!value) return null;
  const entry = STATUS_MAP[value];
  const label = entry ? entry.label : String(value).replace(/_/g, " ");
  const color = entry ? entry.color : NEUTRAL;
  return (
    <Badge
      data-testid={testid}
      className={`border ${color} rounded-full px-2.5 py-0.5 text-xs font-medium`}
    >
      {label}
    </Badge>
  );
}

export function PageHeader({ title, subtitle, actions, testid }) {
  return (
    <div className="flex items-start justify-between flex-wrap gap-3" data-testid={testid}>
      <div className="min-w-0">
        <h1 className="font-display font-bold text-3xl tracking-tight text-[#1C1917]">{title}</h1>
        {subtitle && <p className="text-sm text-[#57534E] mt-1">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 flex-shrink-0">{actions}</div>}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, description, action, testid }) {
  return (
    <div className="text-center py-16 px-6 bg-white rounded-2xl border border-[#E8E2D9]" data-testid={testid}>
      {Icon && (
        <div className="w-14 h-14 rounded-2xl bg-[#FDE9D8] flex items-center justify-center mx-auto mb-4">
          <Icon size={22} className="text-[#E8712C]" strokeWidth={1.5} />
        </div>
      )}
      <h3 className="font-display font-semibold text-lg tracking-tight mb-1">{title}</h3>
      {description && <p className="text-sm text-[#57534E] max-w-sm mx-auto mb-5">{description}</p>}
      {action}
    </div>
  );
}

// Reusable status filter strip. One component for every list page so the
// pattern stays identical (Invoices, Contracts, Service Requests, Bookings…).
// - Horizontally scrollable on narrow screens.
// - Live counts pulled from `counts` map.
// - "All" tab always first, others in the given order.
// - Selected tab renders as orange pill; unselected are text-only.
export function StatusTabs({ tabs, value, onChange, counts, testidPrefix = "status-tab", right = null }) {
  return (
    <div className="flex gap-1 bg-white border border-[#E8E2D9] rounded-full p-1 overflow-x-auto scrollbar-none">
      {tabs.map((t) => {
        const active = value === t.id;
        const n = counts?.[t.id];
        return (
          <button
            key={t.id}
            data-testid={`${testidPrefix}-${t.id}`}
            onClick={() => onChange(t.id)}
            className={`px-3.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors flex-shrink-0 ${
              active ? "bg-[#E8712C] text-white" : "text-[#57534E] hover:bg-[#FAF8F5]"
            }`}
          >
            {t.label}
            {typeof n === "number" && n > 0 && (
              <span className={`ml-1.5 text-[10px] ${active ? "opacity-80" : "opacity-60"}`}>{n}</span>
            )}
          </button>
        );
      })}
      {right && <div className="flex-1 min-w-[8px]" />}
      {right}
    </div>
  );
}

export function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

// Reusable ••• overflow menu for mobile record cards. Wire it into a card's
// top-right corner. The button itself stops-propagation so the surrounding
// card's tap-to-open still works cleanly. Actions arg:
//   [{ label, onClick, icon?, destructive?, hidden?, disabled? }]
// Any action with { destructive: true } gets a confirm() prompt automatically.
export function CardActionMenu({ actions = [], testid, ariaLabel = "More actions" }) {
  const visible = actions.filter((a) => a && !a.hidden);
  if (visible.length === 0) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        asChild
        onClick={(e) => e.stopPropagation()}
      >
        <button
          aria-label={ariaLabel}
          data-testid={testid}
          className="p-2 rounded-full hover:bg-[#F5F1EA] active:bg-[#E8E2D9] transition-colors flex-shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          <MoreVertical size={16} className="text-[#57534E]" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-white border border-[#E8E2D9] rounded-xl min-w-[180px]">
        {visible.map((a, i) => {
          const isSep = a.separator;
          if (isSep) return <DropdownMenuSeparator key={`sep-${i}`} />;
          const Icon = a.icon;
          return (
            <DropdownMenuItem
              key={`${a.label}-${i}`}
              disabled={a.disabled}
              onSelect={(e) => {
                e.preventDefault();
                if (a.destructive) {
                  const msg = a.confirmMessage || `${a.label}? This cannot be undone.`;
                  if (!window.confirm(msg)) return;
                }
                a.onClick?.();
              }}
              data-testid={a.testid}
              className={`cursor-pointer text-sm ${a.destructive ? "text-[#B02222] focus:bg-[#FDDCDC] focus:text-[#B02222]" : ""}`}
            >
              {Icon && <Icon size={14} className="mr-2" />}
              {a.label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
export function formatDateTime(d) {
  if (!d) return "—";
  return new Date(d).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}
export function formatTime(d) {
  if (!d) return "—";
  return new Date(d).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}
